import { useCallback, useEffect, useState } from 'react';
import { fetchMyAchievements, type AchievementFamilyProgress, type MyAchievementsResponse } from './achievementsApi';
import { HopperIcon } from '../ui/icons/HopperIcon';
import './achievements.css';

const CRITERIA_UNITS: Record<string, string> = {
  visits_count: 'визитов',
  visits_in_venue: 'визитов в одном заведении',
  photos_count: 'фото',
  impressions_count: 'впечатлений',
  unique_venues: 'заведений',
  streak_days: 'дней серии',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Дата не указана';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Получено';
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

function FamilyCard({ family }: { family: AchievementFamilyProgress }) {
  const unit = CRITERIA_UNITS[family.criteria_type] ?? 'действий';
  const next = family.next_tier;
  const history = family.tiers.filter((tier) => tier.is_unlocked).slice().reverse();
  const preFirst = family.current_tier === null;
  const maxTier = next === null;

  return (
    <article className={`achievement-family-card${maxTier ? ' is-max' : ''}`}>
      <div className="achievement-family-card__top">
        <div className="achievement-family-card__badge">
          {family.current_tier?.icon_url ? (
            <img src={family.current_tier.icon_url} alt="" />
          ) : next?.icon_url ? (
            <img src={next.icon_url} alt="" className="is-preview" />
          ) : (
            <HopperIcon name="trophy" size={30} />
          )}
        </div>
        <div className="achievement-family-card__identity">
          <span className="achievement-family-card__eyebrow">{family.family_name}</span>
          <h2>{family.current_tier?.name ?? 'Первая ступень впереди'}</h2>
          {maxTier ? <p>Максимальная ступень достигнута</p> : <p>Следующая: {next.name} — {next.target_value} {unit}</p>}
        </div>
        <strong className="achievement-family-card__percent">{family.progress_percent}%</strong>
      </div>

      <div className="achievement-progress" aria-label={`Прогресс ${family.progress_percent}%`}>
        <span style={{ width: `${family.progress_percent}%` }} />
      </div>
      <div className="achievement-progress-copy">
        {maxTier ? (
          <strong>{family.current_value} {unit} · максимум</strong>
        ) : (
          <strong>{family.current_value} / {next.target_value} {unit}</strong>
        )}
        {preFirst && family.current_value > 0 && <span>До Бронзы осталось совсем немного</span>}
        {preFirst && family.current_value === 0 && <span>Начните подтверждать реальные действия в Хоппере</span>}
      </div>

      <div className="achievement-tier-track" aria-label="Ступени достижения">
        {family.tiers.map((tier) => (
          <div className={`achievement-tier-node${tier.is_unlocked ? ' is-unlocked' : ''}`} key={tier.tier_order}>
            <div className="achievement-tier-node__badge">
              {tier.icon_url ? <img src={tier.icon_url} alt="" /> : <HopperIcon name="trophy" size={20} />}
            </div>
            <strong>{tier.name}</strong>
            <span>{tier.target_value}</span>
          </div>
        ))}
      </div>

      <details className="achievement-history" open={maxTier}>
        <summary>История ступеней</summary>
        {history.length === 0 ? (
          <p>Здесь появятся даты полученных ступеней.</p>
        ) : (
          <div className="achievement-history__list">
            {history.map((tier) => (
              <div key={tier.tier_order}>
                <span>{tier.name}</span>
                <time>{formatDate(tier.unlocked_at)}</time>
              </div>
            ))}
          </div>
        )}
      </details>
    </article>
  );
}

export function AchievementsScreen() {
  const [data, setData] = useState<MyAchievementsResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setData(await fetchMyAchievements());
      setState('ready');
    } catch {
      setData(null);
      setState('error');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (state === 'loading') {
    return (
      <main className="achievements-page" aria-label="Достижения Хоппера">
        <div className="achievements-page__head"><span className="achievement-skeleton is-title" /></div>
        <div className="achievement-grid">{Array.from({ length: 6 }, (_, index) => <div className="achievement-family-card achievement-skeleton-card" key={index} />)}</div>
      </main>
    );
  }

  if (state === 'error' || !data) {
    return (
      <main className="achievements-page achievements-page--state" aria-label="Достижения недоступны">
        <section className="achievement-state-card">
          <HopperIcon name="warning" size={28} />
          <strong>Не удалось загрузить достижения</strong>
          <span>Проверьте соединение и попробуйте ещё раз.</span>
          <button type="button" onClick={() => void load()}>Повторить</button>
        </section>
      </main>
    );
  }

  const untouched = data.families.every((family) => family.current_value === 0 && family.current_tier === null);

  return (
    <main className="achievements-page" aria-label="Достижения Хоппера">
      <div className="achievements-nav-row">
        <button className="achievements-back" type="button" onClick={() => window.location.assign('/profile')}>
          <HopperIcon name="arrow-left" size={17} />
          <span>В профиль</span>
        </button>
      </div>

      <header className="achievements-page__head">
        <div>
          <span>Прогресс Хоппера</span>
          <h1>Достижения</h1>
          <p>Каждое реальное действие двигает отдельную линию прогресса. Следующая ступень всегда видна заранее.</p>
        </div>
        <div className="achievements-summary">
          <strong>{data.summary.total_unlocked_tiers}</strong>
          <span>из {data.summary.total_visible_tiers} ступеней открыто</span>
        </div>
      </header>

      {untouched && (
        <section className="achievements-empty">
          <HopperIcon name="sparkle" size={24} />
          <div><strong>История только начинается</strong><span>Подтверждайте визиты, добавляйте фото и впечатления — первые ступени появятся здесь.</span></div>
        </section>
      )}

      <section className="achievement-grid">
        {data.families.map((family) => <FamilyCard family={family} key={family.family_id} />)}
      </section>
    </main>
  );
}
